import React, { useCallback, useMemo, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Stack,
  TextField,
  InputAdornment,
  IconButton,
  Tooltip,
  Chip,
  Snackbar,
  Alert,
  Fab,
  ToggleButton,
  ToggleButtonGroup,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Button,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  DirectionsCar as DirectionsCarIcon,
  Person as PersonIcon,
  Visibility as VisibilityIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  Warning as WarningIcon,
  AccessTime as AccessTimeIcon,
  ErrorOutline as ErrorOutlineIcon,
} from "@mui/icons-material";

import DocumentsForm from "./DocumentsForm";
import { useDocuments } from "../../hooks/useDocuments";
import { useAuthContext } from "../../context/AuthProvider";

const isoDate = (value) => {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toISOString().split("T")[0];
};

const calcDiffDays = (docfecven) => {
  if (!docfecven) return null;
  const today = new Date();
  const venc = new Date(docfecven);
  if (Number.isNaN(venc.getTime())) return null;

  // Normaliza a medianoche para evitar “saltos” por horas
  today.setHours(0, 0, 0, 0);
  venc.setHours(0, 0, 0, 0);

  const diffMs = venc.getTime() - today.getTime();
  return Math.ceil(diffMs / (1000 * 60 * 60 * 24));
};

const DueBadge = ({ diffDays }) => {
  // Solo mostrar si: vencido o < 90 días
  if (diffDays === null) return null;
  if (!(diffDays < 0 || diffDays < 90)) return null;

  let color = "green";
  let bgColor = "#e5fbe5";
  let Icon = AccessTimeIcon;
  let label = `${diffDays} días`;

  if (diffDays < 0) {
    color = "red";
    bgColor = "#ffe5e5";
    Icon = ErrorOutlineIcon;
    label = `Vencido (${Math.abs(diffDays)}d)`;
  } else if (diffDays < 20) {
    color = "red";
    bgColor = "#ffe5e5";
    Icon = ErrorOutlineIcon;
  } else if (diffDays < 40) {
    color = "orange";
    bgColor = "#fff4e5";
    Icon = WarningIcon;
  }

  return (
    <Box
      display="inline-flex"
      alignItems="center"
      gap={0.5}
      px={1}
      py={0.5}
      borderRadius={2}
      sx={{ backgroundColor: bgColor, color, fontWeight: "bold" }}
    >
      <Icon fontSize="small" />
      <Typography variant="body2">{label}</Typography>
    </Box>
  );
};

const DocumentsDashboard = () => {
  const [open, setOpen] = useState(false);
  const [editDocument, setEditDocument] = useState(null);
  const [alert, setAlert] = useState({ open: false, message: "", severity: "success" });

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState(""); // '' | VIGENTE | POR VENCER | VENCIDO

  const [openConfirm, setOpenConfirm] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);

  const [openPreview, setOpenPreview] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);

  const { user } = useAuthContext();
  const { documents = [], isLoading, error, createMutation, updateMutation, deleteMutation } = useDocuments();

  const formDefaultValues = useMemo(() => editDocument ?? {}, [editDocument]);  

  const administradorID = Number(process.env.REACT_APP_ROL_ADMINISTRADOR_ID);
  const auxadministradorID = Number(process.env.REACT_APP_ROL_AUXILIAR_ADMINISTRATIVO_ID);

  const canEdit = [administradorID, auxadministradorID].includes(user?.RolID);
  const canDelete = user?.RolID === administradorID;

  const showAlert = useCallback((message, severity = "success") => {
    setAlert({ open: true, message, severity });
  }, []);

  const handleOpenCreate = useCallback(() => {
    setEditDocument(null);
    setOpen(true);
  }, []);

  const handleOpenEdit = useCallback((doc) => {
    setEditDocument(doc);
    setOpen(true);
  }, []);

  const handleCloseForm = useCallback(() => {
    setOpen(false);
    setEditDocument(null);
  }, []);

  const handleSubmitDocument = useCallback(
    (formData) => {
      if (!user) {
        showAlert("Debe iniciar sesión para realizar esta acción", "error");
        return;
      }

      if (editDocument) {
        formData.append("updbyid", user.EmpID);
        updateMutation.mutate(
          { id: editDocument.docid, data: formData },
          {
            onSuccess: (result) => {
              if (result?.error) return showAlert(result.error, "error");
              showAlert("Documento actualizado con éxito");
              handleCloseForm();
            },
            onError: (err) => {
              const msg = err?.response?.data?.error || "Error al actualizar el documento";
              showAlert(msg, "error");
            },
          }
        );
      } else {
        formData.append("crebyid", user.EmpID);
        createMutation.mutate(formData, {
          onSuccess: (result) => {
            if (result?.error) return showAlert(result.error, "error");
            showAlert("Documento creado con éxito");
            handleCloseForm();
          },
          onError: (err) => {
            const msg = err?.response?.data?.error || "Error al crear el documento";
            showAlert(msg, "error");
          },
        });
      }
    },
    [user, editDocument, updateMutation, createMutation, showAlert, handleCloseForm]
  );

  const handleOpenConfirm = useCallback((doc) => {
    setSelectedDocument(doc);
    setOpenConfirm(true);
  }, []);

  const handleCloseConfirm = useCallback(() => {
    setOpenConfirm(false);
    setSelectedDocument(null);
  }, []);

  const handleConfirmDelete = useCallback(() => {
    if (!selectedDocument) return;

    deleteMutation.mutate(selectedDocument.docid, {
      onSuccess: () => {
        showAlert("Documento eliminado con éxito");
        handleCloseConfirm();
      },
      onError: (err) => {
        showAlert(err?.message || "Error al eliminar el documento", "error");
        handleCloseConfirm();
      },
    });
  }, [selectedDocument, deleteMutation, showAlert, handleCloseConfirm]);

  const handleOpenDocument = useCallback((filePath) => {
    const fullUrl = `${process.env.REACT_APP_API_URL}/documents/file/${filePath}`;
    setPreviewUrl(fullUrl);
    setOpenPreview(true);
  }, []);

  const handleClosePreview = useCallback(() => {
    setOpenPreview(false);
    setPreviewUrl(null);
  }, []);

  const normalizedSearch = search.trim().toLowerCase();

  const rows = useMemo(() => {
    let data = documents;

    if (statusFilter) {
      data = data.filter((d) => d?.docest === statusFilter);
    }

    if (normalizedSearch) {
      data = data.filter((d) => {
        const vehmat = (d?.vehmat || "").toLowerCase();
        const fullName = `${d?.pernom || ""} ${d?.perape || ""}`.toLowerCase();
        const doctip = (d?.doctip || "").toLowerCase();
        return vehmat.includes(normalizedSearch) || fullName.includes(normalizedSearch) || doctip.includes(normalizedSearch);
      });
    }

    return data;
  }, [documents, statusFilter, normalizedSearch]);

  const columns = useMemo(
    () => [
      {
        field: "doctip",
        headerName: "Tipo de Documento",
        flex: 1,
        minWidth: 220,
      },
      {
        field: "asociado",
        headerName: "Asociado a",
        flex: 1,
        minWidth: 220,
        sortable: false,
        valueGetter: (_value, row) => {
          // SOLO texto para búsquedas/orden si quisieras
          if (!row) return "";
          return row.vehid ? row.vehmat || "" : `${row.pernom || ""} ${row.perape || ""}`.trim();
        },
        renderCell: (params) => {
          const row = params.row;
          if (!row) return null;

          const isVeh = !!row.vehid;
          return (
            <Box display="flex" alignItems="center" gap={1} minWidth={0}>
              {isVeh ? (
                <DirectionsCarIcon color="primary" fontSize="small" />
              ) : (
                <PersonIcon color="secondary" fontSize="small" />
              )}
              <Typography variant="body2" fontWeight="bold" noWrap>
                {isVeh ? row.vehmat : `${row.pernom} ${row.perape}`}
              </Typography>
            </Box>
          );
        },
      },
      {
        field: "docfecemi",
        headerName: "Fecha Emisión",
        width: 140,
        valueGetter: (params) => isoDate(params.row?.docfecemi),
      },
      {
        field: "docfecven",
        headerName: "Fecha Vencimiento",
        width: 160,
        valueGetter: (params) => isoDate(params.row?.docfecven),
      },
      {
        field: "venceEn",
        headerName: "Vence En",
        width: 170,
        sortable: true,
        // IMPORTANTE: nunca params.row directo, usa row y null-check
        valueGetter: (_value, row) => calcDiffDays(row?.docfecven) ?? 999999, // para sorting: lo “lejano” al final
        renderCell: (params) => {
          const row = params.row;
          const diffDays = calcDiffDays(row?.docfecven);
          return <DueBadge diffDays={diffDays} />;
        },
      },
      {
        field: "docest",
        headerName: "Estado",
        width: 140,
        renderCell: (params) => {
          const val = params.value;
          return (
            <Chip
              label={val}
              size="small"
              color={val === "VIGENTE" ? "success" : val === "POR VENCER" ? "warning" : "error"}              
            />
          );
        },
      },
      {
        field: "actions",
        headerName: "Acciones",
        width: 160,
        sortable: false,
        filterable: false,
        align: "center",
        headerAlign: "center",
        renderCell: (params) => {
          const row = params.row;
          if (!row) return null;

          return (
            <Stack direction="row" spacing={0.5} justifyContent="center">
              {canEdit && (
                <Tooltip title="Editar">
                  <IconButton size="small" onClick={() => handleOpenEdit(row)}>
                    <EditIcon color="primary" fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}

              {canEdit && row.docrut && (
                <Tooltip title="Ver Documento">
                  <IconButton size="small" onClick={() => handleOpenDocument(row.docrut)}>
                    <VisibilityIcon color="action" fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}

              {canDelete && row.estreg !== "E" && (
                <Tooltip title="Eliminar">
                  <IconButton size="small" onClick={() => handleOpenConfirm(row)} disabled={deleteMutation.isPending}>
                    <DeleteIcon color="secondary" fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Stack>
          );
        },
      },
    ],
    [canEdit, canDelete, handleOpenEdit, handleOpenDocument, handleOpenConfirm, deleteMutation.isPending]
  );

  return (
    <Paper
      sx={{
        p: 2.5,
        borderRadius: 2,
        height: "100vh",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Stack spacing={2} sx={{ flex: 1, minHeight: 0 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h5" fontWeight={700}>
            Documentos
          </Typography>

          <ToggleButtonGroup
            size="small"
            exclusive
            value={statusFilter}
            onChange={(_, val) => setStatusFilter(val ?? "")}
          >
            <ToggleButton value="">Todos</ToggleButton>
            <ToggleButton value="VIGENTE">Vigente</ToggleButton>
            <ToggleButton value="POR VENCER">Por vencer</ToggleButton>
            <ToggleButton value="VENCIDO">Vencido</ToggleButton>
          </ToggleButtonGroup>
        </Box>

        <TextField
          size="small"
          label="Buscar (placa, nombre, tipo documento)"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
            endAdornment: search ? (
              <InputAdornment position="end">
                <Tooltip title="Limpiar">
                  <IconButton size="small" onClick={() => setSearch("")}>
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </InputAdornment>
            ) : null,
          }}
        />

        {error && <Alert severity="error">{error.message || "Error al cargar documentos"}</Alert>}

        <Box sx={{ flex: 1, minHeight: 0 }}>
          <DataGrid
            rows={rows}
            columns={columns}
            loading={isLoading}
            getRowId={(row) => row.docid}
            disableRowSelectionOnClick
            pageSizeOptions={[10, 25, 50, 100]}
            initialState={{
              pagination: { paginationModel: { pageSize: 10, page: 0 } },
              sorting: { sortModel: [{ field: "venceEn", sort: "asc" }] },
            }}
            sx={{
              borderRadius: 2,
              "& .MuiDataGrid-columnHeaders": { fontWeight: 700 },
            }}
          />
        </Box>
      </Stack>

      {canEdit && (
        <Fab
          color="primary"
          aria-label="add"
          sx={{ position: "fixed", bottom: 20, right: 20 }}
          onClick={handleOpenCreate}
        >
          <AddIcon />
        </Fab>
      )}

      {open && (
        <DocumentsForm
          open={open}
          handleClose={handleCloseForm}
          onSubmit={handleSubmitDocument}
          defaultValues={editDocument || {}}
        />
      )}

      {/* Confirm delete */}
      <Dialog open={openConfirm} onClose={handleCloseConfirm}>
        <DialogTitle>Confirmar Eliminación</DialogTitle>
        <DialogContent>
          <DialogContentText>
            ¿Está seguro de que desea eliminar el documento <strong>{selectedDocument?.doctip}</strong>{" "}
            asociado a{" "}
            <strong>
              {selectedDocument?.vehid ? selectedDocument?.vehmat : `${selectedDocument?.pernom} ${selectedDocument?.perape}`}
            </strong>
            ? Esta acción no se puede deshacer.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseConfirm} color="secondary">
            Cancelar
          </Button>
          <Button onClick={handleConfirmDelete} variant="contained" color="error">
            Desactivar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Preview */}
      <Dialog open={openPreview} onClose={handleClosePreview} fullWidth maxWidth="xl">
        <DialogTitle>Vista Previa del Documento</DialogTitle>
        <DialogContent dividers>
          {previewUrl ? (
            previewUrl.endsWith(".pdf") ? (
              <iframe
                src={previewUrl}
                width="100%"
                height="500px"
                style={{ border: "none" }}
                title="Vista previa del documento"
              />
            ) : (
              <img
                src={previewUrl}
                alt="Documento adjunto"
                style={{ width: "100%", maxHeight: "500px", objectFit: "contain" }}
              />
            )
          ) : (
            <p>No hay documento disponible.</p>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePreview} color="secondary">
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={alert.open}
        autoHideDuration={3500}
        onClose={() => setAlert((a) => ({ ...a, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert severity={alert.severity} sx={{ width: "100%" }}>
          {alert.message}
        </Alert>
      </Snackbar>
    </Paper>
  );
};

export default DocumentsDashboard;
